/**
 * The physics of dragging a table: while the interact control is *held* and the crosshair
 * is on a table, the table is towed along behind the player, staying on the ground.
 *
 * The hold is the whole gesture (design D5). A press does nothing to a table —
 * `InteractionSystem` deliberately ignores the `drag` verb — so latching happens here, on
 * the first frame the control is found held with a table in focus and empty hands, and the
 * drag ends the moment it is let go.
 *
 * The offset from player to table is recorded in **world** space at that moment and never
 * recomputed. Turning on the spot therefore moves the table not at all: a player-relative
 * offset would swing a two-metre table around them like a boom, through the hedges on both
 * sides. `src/game/dragging.ts` holds that rule and the leash that ends a hopeless drag.
 *
 * Motion is a per-axis shape cast of the table's own top slab, the same idiom `Player`
 * uses for a carried box: clamped per axis rather than by a contact normal, so a table
 * dragged *along* a hedge still slides instead of stopping dead on first touch.
 */

import { useFrame, useThree } from '@react-three/fiber';
import { useRapier } from '@react-three/rapier';
import { useRef } from 'react';
import { draggedTarget, isLeashBroken } from '../game/dragging';
import { isPlayable } from '../game/phase';
import { inputState } from '../input/inputState';
import { useGameStore } from '../store/gameStore';
import { getObjectBody } from './objectRegistry';
import type { BodyUserData } from './useInteractionTarget';

/** Skin kept between the table's surface and whatever the cast hits. */
const DRAG_SKIN = 0.05;

export function DragController(): null {
  const camera = useThree((state) => state.camera);
  const { world, rapier } = useRapier();

  /** World-space table-minus-player offset, recorded once when the drag latches. */
  const offsetRef = useRef({ x: 0, z: 0 });
  /** The table's own top slab, reused for the casts; resized at each latch. */
  const shapeRef = useRef<InstanceType<typeof rapier.Cuboid> | null>(null);

  useFrame(() => {
    const store = useGameStore.getState();
    if (!isPlayable(store.phase)) return;

    const dragging = store.draggedObjectId;

    if (dragging === null) {
      // Latch. Empty hands and a table under the crosshair: the same conditions
      // `availableAction` reports as the `drag` verb, so the prompt cannot promise a drag
      // that will not start.
      if (!inputState.interactHeld) return;
      if (store.carriedObjectId !== null) return;
      const focus = store.focus;
      if (!focus || focus.kind !== 'table') return;

      const body = getObjectBody(focus.id);
      if (!body) return;

      const table = body.translation();
      offsetRef.current = { x: table.x - camera.position.x, z: table.z - camera.position.z };

      const half = (body.collider(0).shape as { halfExtents?: { x: number; y: number; z: number } })
        .halfExtents;
      if (half) {
        shapeRef.current ??= new rapier.Cuboid(half.x, half.y, half.z);
        shapeRef.current.halfExtents = { x: half.x, y: half.y, z: half.z };
      }

      store.beginDrag(focus.id);
      return;
    }

    // Let go the instant the control comes up — a lifted finger, a released key, or a
    // blur that reset the input while the table was mid-corridor.
    if (!inputState.interactHeld) {
      store.endDrag();
      return;
    }

    const body = getObjectBody(dragging);
    if (!body) {
      store.endDrag();
      return;
    }

    const current = body.translation();
    const target = draggedTarget(
      { x: camera.position.x, z: camera.position.z },
      offsetRef.current,
    );

    const shape = shapeRef.current;
    // Cast from the slab where it actually is: the table's origin is on the ground, and a
    // cast started down there would sweep through the ground plane itself.
    const from = body.collider(0).translation();
    const rotation = body.rotation();

    const clamped = (delta: number, axis: 'x' | 'z') => {
      if (!shape || Math.abs(delta) < 1e-5) return delta;
      const sign = Math.sign(delta);
      const hit = world.castShape(
        from,
        rotation,
        { x: axis === 'x' ? sign : 0, y: 0, z: axis === 'z' ? sign : 0 },
        shape,
        0,
        Math.abs(delta) + DRAG_SKIN,
        true,
        rapier.QueryFilterFlags.EXCLUDE_SENSORS,
        undefined,
        undefined,
        undefined,
        (candidate) => {
          const data = candidate.parent()?.userData as Partial<BodyUserData> | undefined;
          // The player is towing it, not blocking it; and a table cannot obstruct itself.
          if (data?.kind === 'player') return false;
          return !(data?.kind === 'table' && data.id === dragging);
        },
      );
      if (!hit) return delta;
      return sign * Math.min(Math.abs(delta), Math.max(0, hit.time_of_impact - DRAG_SKIN));
    };

    const next = {
      x: current.x + clamped(target.x - current.x, 'x'),
      z: current.z + clamped(target.z - current.z, 'z'),
    };
    body.setNextKinematicTranslation({ x: next.x, y: current.y, z: next.z });

    // A table wedged against a hedge stops; the player does not. Once it has fallen a
    // leash-length behind where it should be, the grip gives out — otherwise the player
    // tows an invisible tether and the table pops back to them when it comes free.
    if (isLeashBroken(next, target)) store.endDrag();
  });

  return null;
}
